// API client for Order Analytics.
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const BASE = `${API_URL}/seller-intelligence/order-analytics`;
const FULFILMENT = `${API_URL}/seller-intelligence/order-fulfillment`;

const authHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

export class ReportUnavailableError extends Error {
  constructor(message) { super(message); this.reportUnavailable = true; }
}

export class NotConnectedError extends Error {
  constructor(message) { super(message); this.notConnected = true; }
}

const call = async (url, options = {}) => {
  const res = await fetch(url, options);
  const data = await res.json().catch(() => ({}));
  if (res.status === 409 && data.error === 'report_unavailable') {
    throw new ReportUnavailableError(data.message);
  }
  if (res.status === 400 && data.error === 'amazon_not_connected') {
    throw new NotConnectedError(data.message);
  }
  if (!res.ok) throw new Error(data.message || `Request failed (${res.status})`);
  return data;
};

// SF1 — Orders by Category. `grain` switches between Amazon's specific browse
// classification and the department above it.
export const getCategories = (token, { start, end, grain } = {}) => {
  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  if (grain) params.set('grain', grain);
  const query = params.toString();
  return call(`${BASE}/categories${query ? `?${query}` : ''}`, {
    headers: authHeaders(token),
  });
};

// SF2 — Orders by Fulfilment Channel.
export const getChannels = (token, { start, end } = {}) => {
  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  const query = params.toString();
  return call(`${BASE}/channels${query ? `?${query}` : ''}`, {
    headers: authHeaders(token),
  });
};

// SF3 — Peak Hours and Days. Everything comes back bucketed in IST.
export const getPeakTimes = (token, { start, end } = {}) => {
  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  const query = params.toString();
  return call(`${BASE}/peak-times${query ? `?${query}` : ''}`, {
    headers: authHeaders(token),
  });
};

// SF4 — Items per Order.
export const getItemsPerOrder = (token, { start, end } = {}) => {
  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  const query = params.toString();
  return call(`${BASE}/items-per-order${query ? `?${query}` : ''}`, {
    headers: authHeaders(token),
  });
};

// SF5 — Repeat Purchase, at product level. A tendency, never a customer count.
export const getRepeatPurchase = (token, { start, end } = {}) => {
  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  const query = params.toString();
  return call(`${BASE}/repeat-purchase${query ? `?${query}` : ''}`, {
    headers: authHeaders(token),
  });
};

// SF6 — Return Rate by SKU. With an asin, returns that product's reason
// brackets instead of the whole table.
export const getReturns = (token, { start, end, asin } = {}) => {
  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  if (asin) params.set('asin', asin);
  const query = params.toString();
  return call(`${BASE}/returns${query ? `?${query}` : ''}`, {
    headers: authHeaders(token),
  });
};

// SF1 of the Fulfillment Dashboard — the operations board. Takes no window:
// it reports state now, and a date filter would hide the oldest open order.
export const getOrderBoard = (token, { refresh } = {}) =>
  call(`${FULFILMENT}/board${refresh ? '?refresh=true' : ''}`, {
    headers: authHeaders(token),
  });

// SF2 — Cancellation Rate Calculator. Analytical, so it takes a window.
export const getCancellations = (token, { start, end } = {}) => {
  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  const query = params.toString();
  return call(`${FULFILMENT}/cancellations${query ? `?${query}` : ''}`, {
    headers: authHeaders(token),
  });
};

// SF3 — Cancellation Attribution and Reasons.
export const getCancellationAttribution = (token, { start, end, asin } = {}) => {
  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  if (asin) params.set('asin', asin);
  const query = params.toString();
  return call(`${FULFILMENT}/cancellation-attribution${query ? `?${query}` : ''}`, {
    headers: authHeaders(token),
  });
};

// SF4 — Average Fulfilment Time, split by shipment type.
export const getFulfilmentTime = (token, { start, end } = {}) => {
  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  const query = params.toString();
  return call(`${FULFILMENT}/fulfilment-time${query ? `?${query}` : ''}`, {
    headers: authHeaders(token),
  });
};

// SF5 — Late Shipment Rate. Takes no window: Amazon defines the reporting
// period for its own account health metrics.
export const getLateShipment = (token) =>
  call(`${FULFILMENT}/late-shipment`, { headers: authHeaders(token) });

// SF6 — Order Defect Rate. Amazon's own figure, on Amazon's own window.
export const getDefectRate = (token) =>
  call(`${FULFILMENT}/defect-rate`, { headers: authHeaders(token) });
